// ============================================================
// SafetyPackagePage.jsx — Paquete Oficial de Seguridad
// URL pública: /paquete-seguridad
// Imprimible como PDF con membrete profesional
// ============================================================
import React, { useState, useEffect } from 'react';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
const LOGO = 'https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.45.52-AM-1.jpeg';
const YEAR = new Date().getFullYear();

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
  : new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });

const TIPO_LABEL = {
  inflable_recreativo: 'Inflable Recreativo',
  pista_obstaculos: 'Pista de Obstáculos',
  juego_mecanico: 'Juego Mecánico Infantil',
  atraccion_recreativa: 'Atracción Recreativa',
  otro: 'Otro',
};

// ── ESTILOS (inline para garantizar impresión correcta) ────────────────────────
const S = {
  // Pantalla
  wrapper: {
    fontFamily: "'Segoe UI', Georgia, serif",
    background: '#e8eaf0',
    minHeight: '100vh',
    padding: '24px 16px 60px',
  },
  controls: {
    maxWidth: 900,
    margin: '0 auto 20px',
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  btnPrint: {
    background: 'linear-gradient(135deg, #4338ca, #6366f1)',
    color: '#fff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px #4338ca40',
  },
  btnClose: {
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    padding: '10px 20px',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
  },
  // Documento (página)
  page: {
    maxWidth: 900,
    margin: '0 auto 32px',
    background: '#fff',
    borderRadius: 4,
    boxShadow: '0 8px 32px #0003',
    pageBreakAfter: 'always',
    overflow: 'hidden',
  },
  // Membrete superior
  header: {
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4338ca 100%)',
    padding: '28px 36px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    color: '#fff',
  },
  logo: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid rgba(255,255,255,0.4)',
    background: '#fff',
  },
  headerTitle: {
    flex: 1,
  },
  companyName: {
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: 1,
    marginBottom: 4,
  },
  companyTagline: {
    fontSize: 13,
    opacity: 0.85,
    letterSpacing: 0.5,
  },
  docTitle: {
    fontSize: 13,
    background: 'rgba(255,255,255,0.18)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 20,
    padding: '4px 14px',
    display: 'inline-block',
    marginTop: 8,
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  // Franja de colores decorativa
  stripe: {
    height: 5,
    background: 'linear-gradient(90deg, #fbbf24, #f59e0b, #4338ca, #6366f1, #34d399)',
  },
  // Cuerpo de cada página
  body: {
    padding: '32px 40px',
  },
  // Sección con título
  sectionTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: '#1e1b4b',
    borderLeft: '4px solid #4338ca',
    paddingLeft: 12,
    marginBottom: 16,
    marginTop: 28,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // Tabla de datos
  dataGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 24px',
    marginBottom: 16,
  },
  dataItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  dataLabel: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  dataValue: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: 500,
    borderBottom: '1px dotted #e2e8f0',
    paddingBottom: 2,
  },
  // Checklist
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 0',
    borderBottom: '1px solid #f8fafc',
    fontSize: 13,
    color: '#334155',
  },
  checkMark: {
    width: 20,
    height: 20,
    background: '#dcfce7',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: '#16a34a',
    flexShrink: 0,
    fontWeight: 700,
  },
  // Badge resultado
  badge: (result) => ({
    display: 'inline-block',
    padding: '4px 14px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    background: result === 'aprobado' ? '#dcfce7' : result === 'aprobado_con_obs' ? '#fef9c3' : '#fee2e2',
    color: result === 'aprobado' ? '#16a34a' : result === 'aprobado_con_obs' ? '#d97706' : '#dc2626',
  }),
  // Firma
  firmaBox: {
    borderTop: '1px solid #334155',
    paddingTop: 8,
    marginTop: 40,
    width: 220,
    fontSize: 12,
    color: '#475569',
    textAlign: 'center',
  },
  // Footer de página
  footer: {
    background: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    padding: '12px 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 11,
    color: '#94a3b8',
  },
  // Portada grande
  coverPage: {
    textAlign: 'center',
    padding: '60px 40px',
    background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)',
  },
  coverLogo: {
    width: 120,
    height: 120,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '4px solid #4338ca',
    marginBottom: 24,
  },
  coverTitle: {
    fontSize: 28,
    fontWeight: 800,
    color: '#1e1b4b',
    lineHeight: 1.2,
    marginBottom: 12,
  },
  coverSubtitle: {
    fontSize: 16,
    color: '#4338ca',
    fontWeight: 600,
    marginBottom: 32,
  },
  infoBox: {
    display: 'inline-block',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '20px 32px',
    textAlign: 'left',
    marginBottom: 32,
  },
  infoRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 8,
    fontSize: 13,
    color: '#475569',
  },
  infoLabel: {
    fontWeight: 700,
    color: '#1e293b',
    minWidth: 140,
  },
  divider: {
    height: 2,
    background: 'linear-gradient(90deg, #4338ca, #6366f1, #34d399)',
    borderRadius: 2,
    margin: '24px 0',
  },
  equipCard: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    marginBottom: 20,
    overflow: 'hidden',
  },
  equipCardHeader: {
    background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
    color: '#fff',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};

// ── Membrete reutilizable ──────────────────────────────────────────────────────
function PageHeader({ title, pageNum }) {
  return (
    <>
      <div style={S.header}>
        <img src={LOGO} alt="Logo" style={S.logo} onError={e => e.target.style.display='none'} />
        <div style={S.headerTitle}>
          <div style={S.companyName}>INFLABLES DA SILVA · DA SILVA FUNPARK</div>
          <div style={S.companyTagline}>Juegos Inflables, Mecánicos y Recreativos · Chía, Cundinamarca · Desde 1988</div>
          <div style={S.docTitle}>{title}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12, opacity: 0.8 }}>
          <div style={{ fontWeight: 700 }}>Pág. {pageNum}</div>
          <div>Año {YEAR}</div>
        </div>
      </div>
      <div style={S.stripe} />
    </>
  );
}

function PageFooter({ text }) {
  return (
    <div style={S.footer}>
      <span>📧 cotizaciones@inflablesdasilva.com · 📱 +57 333 400 8723</span>
      <span>{text || 'INFLABLES DA SILVA — Documento Confidencial'}</span>
      <span>Año {YEAR}</span>
    </div>
  );
}

// ── PÁGINA 1: PORTADA ──────────────────────────────────────────────────────────
function PaginaPortada() {
  return (
    <div style={S.page}>
      <div style={S.stripe} />
      <div style={S.coverPage}>
        <img src={LOGO} alt="Logo" style={S.coverLogo} onError={e => e.target.style.display='none'} />
        <div style={S.coverTitle}>
          PAQUETE OFICIAL<br />DE SEGURIDAD Y CALIDAD
        </div>
        <div style={S.coverSubtitle}>
          INFLABLES DA SILVA · DA SILVA FUNPARK
        </div>
        <div style={S.divider} />
        <div style={S.infoBox}>
          <div style={S.infoRow}><span style={S.infoLabel}>Empresa:</span><span>INFLABLES DA SILVA / DA SILVA FUNPARK</span></div>
          <div style={S.infoRow}><span style={S.infoLabel}>Actividad:</span><span>Alquiler y operación de juegos inflables, mecánicos y recreativos</span></div>
          <div style={S.infoRow}><span style={S.infoLabel}>Ciudad:</span><span>Chía, Cundinamarca — Colombia</span></div>
          <div style={S.infoRow}><span style={S.infoLabel}>Teléfono:</span><span>+57 333 400 8723</span></div>
          <div style={S.infoRow}><span style={S.infoLabel}>Correo:</span><span>cotizaciones@inflablesdasilva.com</span></div>
          <div style={S.infoRow}><span style={S.infoLabel}>Web:</span><span>www.inflablesdasilva.com</span></div>
          <div style={S.infoRow}><span style={S.infoLabel}>Año vigencia:</span><span style={{ fontWeight: 700, color: '#4338ca' }}>{YEAR}</span></div>
        </div>
        <div style={{ fontSize: 13, color: '#64748b', maxWidth: 540, margin: '0 auto', lineHeight: 1.7 }}>
          Este documento contiene los protocolos de seguridad, certificaciones técnicas de equipos
          y normas de operación que garantizan la seguridad de todos los usuarios durante
          los eventos realizados por INFLABLES DA SILVA.
        </div>
        <div style={{ marginTop: 40 }}>
          <div style={{ display: 'inline-flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
            {['✅ Protocolo de Seguridad', '📋 Normas de Uso', '🛡️ Póliza RC $300M', '🔧 Certificación Técnica', '🦺 Checklist de Operación'].map(t => (
              <div key={t} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{t}</div>
            ))}
          </div>
        </div>
      </div>
      <div style={S.stripe} />
      <PageFooter text="Documento oficial — uso exclusivo para clientes y aliados" />
    </div>
  );
}

// ── PÁGINA 2: PROTOCOLO DE SEGURIDAD ──────────────────────────────────────────
function PaginaProtocolo() {
  const items = [
    { icon: '👷', title: 'Personal capacitado', desc: 'Todos nuestros operarios reciben entrenamiento específico en montaje, operación segura y atención a usuarios. Cuentan con contrato de prestación de servicios y están afiliados a ARL para cada evento.' },
    { icon: '🔍', title: 'Revisión previa al evento', desc: 'Antes de iniciar cualquier evento, el operario realiza una inspección completa del equipo: inflado, ventilador, anclajes, conexiones eléctricas y estado general del material.' },
    { icon: '👁️', title: 'Supervisión permanente', desc: 'Durante toda la operación, un operario capacitado permanece junto al equipo controlando el número de usuarios, el cumplimiento de normas y cualquier situación de riesgo.' },
    { icon: '🚨', title: 'Protocolo de emergencia', desc: 'En caso de accidente o emergencia: detener la operación de inmediato, atender al afectado, llamar a emergencias (123) si es necesario, y notificar al supervisor de DA SILVA. Se diligencia el Formato de Reporte de Incidentes.' },
    { icon: '⚡', title: 'Seguridad eléctrica', desc: 'Los ventiladores y equipos eléctricos cuentan con certificación RETIE. Los cables están protegidos y alejados de las zonas de uso. No se opera con equipos mojados o bajo lluvia intensa.' },
    { icon: '🌧️', title: 'Condiciones climáticas', desc: 'Los inflables no se operan bajo lluvia fuerte o viento que supere condiciones seguras. El operario puede y debe detener la operación si las condiciones climáticas representan riesgo.' },
  ];

  return (
    <div style={S.page}>
      <PageHeader title="Protocolo de Seguridad" pageNum={2} />
      <div style={S.body}>
        <div style={S.sectionTitle}>Compromisos de Seguridad</div>
        <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 20 }}>
          INFLABLES DA SILVA / DA SILVA FUNPARK se compromete a operar bajo estándares de seguridad
          que garantizan la integridad física de todos los usuarios durante los eventos. A continuación
          se describen los pilares de nuestro protocolo de seguridad:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {items.map(item => (
            <div key={item.title} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', background: '#fafafa' }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1e1b4b', marginBottom: 6 }}>{item.title}</div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>{item.desc}</div>
            </div>
          ))}
        </div>

        <div style={S.divider} />
        <div style={S.sectionTitle}>Declaración de Compromiso</div>
        <div style={{ border: '2px solid #4338ca', borderRadius: 8, padding: '16px 20px', background: '#f5f3ff' }}>
          <p style={{ fontSize: 13, color: '#1e1b4b', lineHeight: 1.8, margin: 0, fontStyle: 'italic' }}>
            "INFLABLES DA SILVA / DA SILVA FUNPARK garantiza que todos los equipos utilizados en sus
            eventos han sido revisados técnicamente y son operados por personal capacitado y comprometido
            con la seguridad de los usuarios. Ante cualquier situación que comprometa la seguridad,
            nuestra prioridad es siempre la integridad de las personas."
          </p>
          <div style={{ marginTop: 12, fontSize: 12, color: '#6366f1', fontWeight: 700 }}>
            — Dirección General, INFLABLES DA SILVA / DA SILVA FUNPARK
          </div>
        </div>
      </div>
      <PageFooter text="Protocolo de Seguridad — Vigencia {YEAR}" />
    </div>
  );
}

// ── PÁGINA 3: NORMAS DE USO PARA USUARIOS ─────────────────────────────────────
function PaginaNormas() {
  const normasInflables = [
    'Retirarse los zapatos antes de ingresar al inflable',
    'No ingresar con objetos cortopunzantes (cinturones, llaves, aretes grandes)',
    'No ingresar con alimentos o bebidas',
    'Respetar el número máximo de usuarios permitido',
    'No saltar sobre otros usuarios ni empujarse',
    'Los menores de 3 años deben ir acompañados por un adulto',
    'No realizar acrobacias ni saltos riesgosos',
    'En caso de mal tiempo, desocupar el inflable inmediatamente',
    'Seguir siempre las indicaciones del operario',
    'Reportar cualquier daño o situación riesgosa al operario',
  ];

  const normasMecanicos = [
    'Cumplir los límites de edad, peso y estatura indicados en cada juego',
    'No soltarse del equipo de seguridad durante la operación',
    'Mantener las manos y pies dentro de las zonas designadas',
    'No operar el juego sin supervisión del operario',
    'Informar al operario si siente mareos o malestar',
  ];

  return (
    <div style={S.page}>
      <PageHeader title="Normas de Uso para Usuarios" pageNum={3} />
      <div style={S.body}>
        <div style={S.sectionTitle}>🎈 Normas para Juegos Inflables</div>
        <div style={{ columns: 2, gap: 20 }}>
          {normasInflables.map((n, i) => (
            <div key={i} style={S.checkItem}>
              <div style={S.checkMark}>✓</div>
              <span>{n}</span>
            </div>
          ))}
        </div>

        <div style={S.sectionTitle}>⚙️ Normas para Juegos Mecánicos</div>
        {normasMecanicos.map((n, i) => (
          <div key={i} style={S.checkItem}>
            <div style={S.checkMark}>✓</div>
            <span>{n}</span>
          </div>
        ))}

        <div style={S.divider} />
        <div style={{ background: '#fef9c3', border: '1px solid #fbbf24', borderRadius: 8, padding: '14px 18px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#d97706', marginBottom: 6 }}>⚠️ RESTRICCIONES IMPORTANTES</div>
          <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.7 }}>
            El incumplimiento de estas normas puede resultar en accidentes. El operario tiene
            la autoridad para retirar del juego a cualquier usuario que no cumpla las normas
            o que represente un riesgo para los demás. INFLABLES DA SILVA no se hace responsable
            de accidentes derivados del incumplimiento de estas normas.
          </div>
        </div>

        <div style={S.sectionTitle}>📞 Contacto en Caso de Emergencia</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {[['🚨 Emergencias', '123'], ['🏥 Cruz Roja', '132'], ['🚒 Bomberos', '119'], ['📱 DA SILVA', '+57 333 400 8723']].map(([l, v]) => (
            <div key={l} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 16px', textAlign: 'center', minWidth: 120 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e1b4b' }}>{l}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#4338ca' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
      <PageFooter text="Normas de Seguridad — Año {YEAR}" />
    </div>
  );
}

// ── PÁGINA 4: PÓLIZA DE RESPONSABILIDAD CIVIL ─────────────────────────────────
function PaginaPoliza() {
  return (
    <div style={S.page}>
      <PageHeader title="Póliza de Responsabilidad Civil" pageNum={4} />
      <div style={S.body}>
        <div style={{ textAlign: 'center', margin: '0 0 32px' }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>🛡️</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1e1b4b', marginBottom: 4 }}>
            PÓLIZA DE RESPONSABILIDAD CIVIL
          </div>
          <div style={{ fontSize: 14, color: '#4338ca', fontWeight: 600 }}>
            Protección total para nuestros clientes y sus invitados
          </div>
        </div>

        {/* Datos de la póliza */}
        <div style={{
          background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4338ca 100%)',
          borderRadius: 12,
          padding: '24px 32px',
          color: '#fff',
          marginBottom: 28,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Aseguradora</div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>SURA</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>No. de Póliza</div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>900000560301</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Vencimiento</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Junio 25 de 2026</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Suma Asegurada</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>$300.000.000</div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 12, fontSize: 12, opacity: 0.8 }}>
            Póliza vigente para todos los eventos operados por INFLABLES DA SILVA / DA SILVA FUNPARK
          </div>
        </div>

        {/* Qué cubre */}
        <div style={S.sectionTitle}>¿Qué cubre la póliza?</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
          {[
            { icon: '🤕', title: 'Accidentes de usuarios', desc: 'Lesiones o daños físicos sufridos por los participantes durante el uso de los equipos recreativos.' },
            { icon: '🏗️', title: 'Daños a terceros', desc: 'Daños materiales o corporales causados a terceras personas o propiedades durante la operación del evento.' },
            { icon: '⚡', title: 'Daños por operación', desc: 'Incidentes relacionados con la operación, montaje o desmontaje de los equipos recreativos.' },
            { icon: '🏥', title: 'Gastos médicos', desc: 'Cubre gastos de atención médica de emergencia derivados de accidentes durante el evento.' },
          ].map(item => (
            <div key={item.title} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', background: '#fafafa' }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e1b4b', marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>{item.desc}</div>
            </div>
          ))}
        </div>

        <div style={S.divider} />

        {/* Cuándo aplica */}
        <div style={S.sectionTitle}>¿Cuándo aplica la cobertura?</div>
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
          {[
            'La póliza está activa durante todos los eventos donde INFLABLES DA SILVA opera sus equipos',
            'Cubre desde el momento del montaje hasta el desmontaje completo del equipo',
            'Aplica para todos los equipos incluidos en la orden de servicio correspondiente',
            'La cobertura es válida siempre que se respeten las normas de uso establecidas',
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
              <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 13, color: '#166534', lineHeight: 1.6 }}>{item}</span>
            </div>
          ))}
        </div>

        {/* Nota legal */}
        <div style={{ background: '#fef9c3', border: '1px solid #fbbf24', borderRadius: 8, padding: '14px 18px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#d97706', marginBottom: 6 }}>📋 Nota importante</div>
          <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.7 }}>
            La cobertura aplica para eventos realizados dentro del territorio colombiano bajo operación
            directa de INFLABLES DA SILVA. Los detalles, exclusiones y condiciones específicas de la
            póliza son responsabilidad de la aseguradora. Ante cualquier incidente, notificar
            inmediatamente al supervisor de DA SILVA al <strong>+57 333 400 8723</strong>.
          </div>
        </div>
      </div>
      <PageFooter text="Póliza de Responsabilidad Civil — Vigente {YEAR}" />
    </div>
  );
}

// ── PÁGINA 5+: CERTIFICADOS DE EQUIPOS ────────────────────────────────────────
function PaginaCertificados({ certs }) {
  if (!certs || certs.length === 0) {
    return (
      <div style={S.page}>
        <PageHeader title="Certificados Técnicos" pageNum={5} />
        <div style={{ ...S.body, textAlign: 'center', color: '#94a3b8', padding: '60px 40px' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📋</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Certificados técnicos en proceso de registro</div>
          <div style={{ fontSize: 13, marginTop: 8 }}>Todos nuestros equipos son revisados anualmente por personal técnico especializado.</div>
        </div>
        <PageFooter text="Certificados Técnicos — Año {YEAR}" />
      </div>
    );
  }

  // Agrupar en páginas de 3 equipos por hoja
  const chunks = [];
  for (let i = 0; i < certs.length; i += 3) chunks.push(certs.slice(i, i + 3));

  return chunks.map((chunk, pi) => (
    <div key={pi} style={S.page}>
      <PageHeader title="Certificados de Revisión Técnica" pageNum={5 + pi} />
      <div style={S.body}>
        <div style={S.sectionTitle}>🔧 Equipos Certificados — Año {YEAR}</div>
        {chunk.map((cert, i) => (
          <div key={cert._id || i} style={S.equipCard}>
            <div style={S.equipCardHeader}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{cert.equipoNombre}</span>
                {cert.equipoRef && <span style={{ fontSize: 12, opacity: 0.8, marginLeft: 10 }}>Ref: {cert.equipoRef}</span>}
              </div>
              <span style={S.badge(cert.resultadoGeneral)}>
                {cert.resultadoGeneral === 'aprobado' ? '✓ APROBADO' : cert.resultadoGeneral === 'aprobado_con_obs' ? '⚠ CON OBSERVACIONES' : '✗ RECHAZADO'}
              </span>
            </div>
            <div style={{ padding: '12px 16px', display: 'flex', gap: 24 }}>
              {cert.fotoUrl && (
                <img src={cert.fotoUrl} alt={cert.equipoNombre}
                  style={{ width: 90, height: 70, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                  onError={e => e.target.style.display = 'none'} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 12px', marginBottom: 10 }}>
                  <div><span style={S.dataLabel}>Tipo</span><br /><span style={{ fontSize: 12 }}>{TIPO_LABEL[cert.tipoEquipo] || cert.tipoEquipo}</span></div>
                  {cert.capacidadMaxima > 0 && <div><span style={S.dataLabel}>Carga máx. total</span><br /><span style={{ fontSize: 12 }}>{cert.capacidadMaxima} kg</span></div>}
                  {cert.dimensiones && <div><span style={S.dataLabel}>Dimensiones</span><br /><span style={{ fontSize: 12 }}>{cert.dimensiones}</span></div>}
                  <div><span style={S.dataLabel}>Fecha revisión</span><br /><span style={{ fontSize: 12 }}>{fmtDate(cert.fechaRevision)}</span></div>
                  {cert.tecnicoNombre && <div><span style={S.dataLabel}>Técnico</span><br /><span style={{ fontSize: 12 }}>{cert.tecnicoNombre}</span></div>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(cert.itemsInspeccion || []).filter(it => it.checked).slice(0, 6).map((it, j) => (
                    <span key={j} style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                      ✓ {it.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <PageFooter text={`Certificados Técnicos — Página ${5 + pi}`} />
    </div>
  ));
}

// ── PÁGINA FINAL: CHECKLIST DE OPERACIÓN + DECLARACIÓN ────────────────────────
function PaginaFinal({ pageNum }) {
  const checklist = [
    'Inspección completa del equipo antes del evento',
    'Verificación del ventilador y sistema eléctrico',
    'Instalación y verificación de anclajes',
    'Delimitación del área de seguridad perimetral',
    'Verificación de condiciones climáticas',
    'Briefing de normas a los usuarios antes de iniciar',
    'Supervisión permanente durante toda la operación',
    'Inspección final y reporte de novedades al cerrar',
  ];

  return (
    <div style={S.page}>
      <PageHeader title="Checklist de Operación · Declaración" pageNum={pageNum} />
      <div style={S.body}>
        <div style={S.sectionTitle}>🦺 Checklist de Operación por Evento</div>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          El operario de DA SILVA verifica y ejecuta los siguientes puntos en cada evento:
        </p>
        <div style={{ columns: 2, gap: 20 }}>
          {checklist.map((item, i) => (
            <div key={i} style={{ ...S.checkItem, breakInside: 'avoid' }}>
              <div style={{ ...S.checkMark, background: '#dbeafe', color: '#1d4ed8' }}>{i + 1}</div>
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div style={S.divider} />
        <div style={S.sectionTitle}>📄 Declaración Oficial</div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '20px 24px', background: '#fafafa', fontSize: 13, lineHeight: 1.8, color: '#334155' }}>
          <p style={{ margin: '0 0 12px' }}>
            Por medio del presente documento, <strong>INFLABLES DA SILVA / DA SILVA FUNPARK</strong> declara que:
          </p>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Todos los equipos presentados en este paquete han sido inspeccionados técnicamente y se encuentran en condiciones seguras para su operación.</li>
            <li>El personal asignado a los eventos cuenta con la formación necesaria para operar los equipos de forma segura.</li>
            <li>Se cuenta con póliza de responsabilidad civil por <strong>$300.000.000</strong> (trescientos millones de pesos) que cubre accidentes de usuarios y daños a terceros durante todos los eventos.</li>
            <li>Se cumplen con los protocolos de seguridad descritos en este documento.</li>
          </ol>
        </div>

        <div style={{ marginTop: 40, display: 'flex', justifyContent: 'space-around' }}>
          <div style={S.firmaBox}>
            <div style={{ height: 50 }}></div>
            <div style={{ fontWeight: 700, color: '#1e1b4b', fontSize: 13 }}>Carlos Da Silva</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Director General</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>INFLABLES DA SILVA</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 8, fontSize: 20 }}>🏢</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Bogotá D.C., {fmtDate(new Date())}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Documento vigente año {YEAR}</div>
            <div style={{ marginTop: 12 }}>
              <img src={LOGO} alt="Logo" style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: '2px solid #4338ca' }} onError={e => e.target.style.display='none'} />
            </div>
          </div>
          <div style={S.firmaBox}>
            <div style={{ height: 50 }}></div>
            <div style={{ fontWeight: 700, color: '#1e1b4b', fontSize: 13 }}>Responsable Técnico</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Supervisor de Operaciones</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>INFLABLES DA SILVA</div>
          </div>
        </div>
      </div>
      <PageFooter text="© INFLABLES DA SILVA — Documento Oficial de Seguridad" />
    </div>
  );
}

// ── PÁGINA: BITÁCORA DE MANTENIMIENTO ────────────────────────────────────────
function PaginaBitacora({ summary, pageNum }) {
  const fmtShort = (d) => d ? new Date(d).toLocaleDateString('es-CO') : '—';
  const TIPO_COLOR = { preventivo: '#4338ca', inspeccion: '#059669', correctivo: '#d97706', emergencia: '#dc2626' };
  const TIPO_LABEL = { preventivo: 'Preventivo', inspeccion: 'Inspección', correctivo: 'Correctivo', emergencia: 'Emergencia' };

  return (
    <div style={S.page}>
      <PageHeader title="Bitácora de Mantenimiento" pageNum={pageNum} />
      <div style={S.body}>
        <div style={S.sectionTitle}>🔧 Registro de Mantenimientos — Últimos 3 meses</div>

        {/* Resumen estadístico */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
          {[
            { icon: '📦', label: 'Equipos en programa', value: summary.totalEquipos },
            { icon: '🔧', label: 'Registros últimos 3 meses', value: summary.totalRegistros },
            { icon: '📅', label: 'Frecuencia', value: 'Mensual' },
          ].map(({ icon, label, value }) => (
            <div key={label} style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', borderRadius: 10, padding: '16px 20px', color: '#fff', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>{icon}</div>
              <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 4 }}>{value}</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>{label}</div>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.7, marginBottom: 16 }}>
          Los mantenimientos preventivos son realizados con periodicidad mensual a la totalidad de los equipos
          del inventario, en cumplimiento del Artículo 24 de la Resolución 958/2010.
          Cada registro incluye inspección de material, sistema de anclaje, instalación eléctrica y prueba de inflado.
        </p>

        {/* Tabla de registros */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', color: '#fff' }}>
                {['Fecha', 'Código', 'Equipo', 'Tipo', 'Descripción', 'Técnico'].map(h => (
                  <th key={h} style={{ padding: '7px 8px', textAlign: 'left', fontWeight: 700, fontSize: 10, letterSpacing: 0.3 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(summary.registros || []).map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '5px 8px', color: '#475569', whiteSpace: 'nowrap' }}>{fmtShort(r.fecha)}</td>
                  <td style={{ padding: '5px 8px', color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.codigo || '—'}</td>
                  <td style={{ padding: '5px 8px', color: '#1e293b', fontWeight: 500 }}>{(r.equipo || '—').slice(0, 30)}</td>
                  <td style={{ padding: '5px 8px' }}>
                    <span style={{ background: (TIPO_COLOR[r.tipo] || '#64748b') + '20', color: TIPO_COLOR[r.tipo] || '#64748b', padding: '2px 7px', borderRadius: 8, fontWeight: 700, fontSize: 10 }}>
                      {TIPO_LABEL[r.tipo] || r.tipo}
                    </span>
                  </td>
                  <td style={{ padding: '5px 8px', color: '#475569', maxWidth: 200 }}>{(r.descripcion || '—').slice(0, 60)}</td>
                  <td style={{ padding: '5px 8px', color: '#64748b', whiteSpace: 'nowrap' }}>{(r.tecnico || '—').slice(0, 25)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 16, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>
            ✅ Programa de mantenimiento vigente — Resolución 958/2010, Capítulo IV, Art. 24 — Cumplimiento: 100%
          </div>
        </div>
      </div>
      <PageFooter text="Bitácora de Mantenimiento — Res. 958/2010" />
    </div>
  );
}

// ── PÁGINA: CHECKLISTS PRE-EVENTO ─────────────────────────────────────────────
function PaginaChecklists({ checklists, pageNum }) {
  const fmtShort = (d) => d ? new Date(d).toLocaleDateString('es-CO') : '—';
  const ITEMS_CHECKLIST = [
    'Superficie/locación adecuada según fabricante',
    'Barreras de control de público en posición',
    'Sistema de anclajes intacto — lazos sin desgaste',
    'Todos los puntos de anclaje asegurados',
    'Sin perforaciones ni costuras abiertas',
    'Motor adecuado y presión de aire correcta',
    'Sin contactos eléctricos expuestos',
    'Aislamiento de cables en buenas condiciones',
    'Clavijas, tomas e interruptores cubiertos',
    'Sin líneas eléctricas a menos de 15m',
    'Cerrojos y tornillos del motor asegurados',
    'Tubo de inflado sujeto firmemente',
    'Motor ubicado sin obstruir entradas/salidas',
    'Viento aceptable (< 15 km/h)',
    'Sin lluvia — equipo apto para operar',
  ];

  return (
    <div style={S.page}>
      <PageHeader title="Checklists Pre-Evento — Res. 958/2010 Art. 26" pageNum={pageNum} />
      <div style={S.body}>
        <div style={S.sectionTitle}>📋 Verificación Pre-Evento por Equipo</div>
        <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.7, marginBottom: 16 }}>
          Antes de cada despliegue, el operario verifica los siguientes 15 puntos de seguridad
          conforme al Artículo 26 §1 de la Resolución 958/2010. El equipo solo opera si todos los
          ítems están aprobados.
        </p>

        {/* Ítems del checklist estándar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', marginBottom: 20 }}>
          {ITEMS_CHECKLIST.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0', borderBottom: '1px solid #f8fafc', fontSize: 12, color: '#334155' }}>
              <span style={{ color: '#16a34a', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
              <span>{item}</span>
            </div>
          ))}
        </div>

        {/* Registros recientes */}
        {checklists && checklists.length > 0 && (
          <>
            <div style={S.sectionTitle}>📁 Registros de Checklists Emitidos</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', color: '#fff' }}>
                  {['Fecha', 'Equipo', 'Orden', 'Ciudad', 'Operario', 'Resultado'].map(h => (
                    <th key={h} style={{ padding: '7px 8px', textAlign: 'left', fontWeight: 700, fontSize: 10 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {checklists.map((c, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '5px 8px', color: '#475569', whiteSpace: 'nowrap' }}>{fmtShort(c.eventDate || c.createdAt)}</td>
                    <td style={{ padding: '5px 8px', color: '#1e293b', fontWeight: 500 }}>{(c.equipmentName || '—').slice(0, 28)}</td>
                    <td style={{ padding: '5px 8px', color: '#6366f1' }}>{c.orderNumber || '—'}</td>
                    <td style={{ padding: '5px 8px', color: '#475569' }}>{(c.city || '—').slice(0, 15)}</td>
                    <td style={{ padding: '5px 8px', color: '#475569' }}>{(c.operario || '—').slice(0, 18)}</td>
                    <td style={{ padding: '5px 8px' }}>
                      <span style={{
                        background: c.resultado === 'aprobado' ? '#dcfce7' : c.resultado === 'rechazado' ? '#fee2e2' : '#fef9c3',
                        color: c.resultado === 'aprobado' ? '#16a34a' : c.resultado === 'rechazado' ? '#dc2626' : '#d97706',
                        padding: '2px 7px', borderRadius: 8, fontWeight: 700, fontSize: 10
                      }}>
                        {c.resultado === 'aprobado' ? '✅ Aprobado' : c.resultado === 'rechazado' ? '❌ Rechazado' : '⏳ Pendiente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {(!checklists || checklists.length === 0) && (
          <div style={{ background: '#fef9c3', border: '1px solid #fbbf24', borderRadius: 8, padding: '14px 18px', fontSize: 12, color: '#92400e' }}>
            Los checklists pre-evento se registran en el sistema antes de cada evento. El formato estándar
            de 15 ítems está disponible en el módulo de Seguridad del sistema de gestión.
          </div>
        )}
      </div>
      <PageFooter text="Checklists Pre-Evento — Art. 26 Res. 958/2010" />
    </div>
  );
}

// ── ESTILOS PARA IMPRESIÓN (inyectados en <head>) ─────────────────────────────
const PRINT_CSS = `
  @media print {
    body { margin: 0; background: #fff !important; }
    .no-print { display: none !important; }
    .page-wrap { box-shadow: none !important; border-radius: 0 !important; margin: 0 !important; }
    @page { margin: 0; size: A4 portrait; }
  }
`;

// ── COMPONENTE PRINCIPAL ───────────────────────────────────────────────────────
// ── PÁGINA: FICHAS TÉCNICAS DE EQUIPOS ALQUILADOS ─────────────────────────────
function PaginaFichasTecnicas({ equipos, startPageNum }) {
  if (!equipos || equipos.length === 0) return null;

  return equipos.map((eq, i) => (
    <div key={i} style={S.page}>
      <PageHeader title={`Ficha Técnica — ${eq.nombre}`} pageNum={startPageNum + i} />
      <div style={S.body}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', marginBottom: 20 }}>
          {eq.foto && (
            <img src={eq.foto} alt={eq.nombre}
              style={{ width: 140, height: 110, objectFit: 'cover', borderRadius: 8, border: '2px solid #e2e8f0', flexShrink: 0 }}
              onError={e => e.target.style.display = 'none'} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1e1b4b', marginBottom: 4 }}>{eq.nombre}</div>
            {eq.codigo && <div style={{ fontSize: 12, color: '#6366f1', fontWeight: 600, marginBottom: 4 }}>Ref: {eq.codigo}</div>}
            {eq.categoria && <div style={{ fontSize: 12, color: '#64748b' }}>Categoría: {eq.categoria}</div>}
          </div>
        </div>

        {eq.descripcion && (
          <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, marginBottom: 16, background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
            {eq.descripcion}
          </div>
        )}

        <div style={S.sectionTitle}>📐 Especificaciones Técnicas</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginBottom: 20 }}>
          {[
            ['Dimensiones / Medidas', eq.medidas],
            ['Edades permitidas', eq.edades],
            ['Operarios requeridos', eq.operarios ? `${eq.operarios} operario(s)` : null],
            ['Capacidad máx. personas', eq.capacidadPersonas ? `${eq.capacidadPersonas} personas` : null],
            ['Peso máx. usuario', eq.pesoMaxUsuario ? `${eq.pesoMaxUsuario} kg` : null],
            ['Altura máx. usuario', eq.alturaMaxUsuario],
            ['Viento máximo', eq.vientoMaxKmh ? `${eq.vientoMaxKmh} km/h` : null],
            ['Tiempo de montaje', eq.tiempoMontajeMin ? `${eq.tiempoMontajeMin} minutos` : null],
            ['Espacio libre requerido', eq.espacioLibreM],
            ['Tipo de anclaje', eq.tipoAnclaje],
            ['Material', eq.material],
            ['Norma técnica', eq.norma],
            ['Área total requerida', eq.areaRequerida],
            ['Volumen (transporte)', eq.volumen_m3 ? `${eq.volumen_m3} m³` : null],
            ['Fabricante', eq.fabricante],
            ['País de origen', eq.paisOrigen],
            ['Vida útil estimada', eq.vidaUtil],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label} style={S.dataItem}>
              <span style={S.dataLabel}>{label}</span>
              <span style={S.dataValue}>{value}</span>
            </div>
          ))}
        </div>

        {(eq.voltaje || eq.amperaje || eq.tipoConector || eq.tipoInflador || eq.requiereElectrico) && (
          <>
            <div style={S.sectionTitle}>⚡ Requerimientos Eléctricos</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginBottom: 20 }}>
              {[
                ['Requiere conexión eléctrica', eq.requiereElectrico ? 'Sí' : 'No'],
                ['Voltaje', eq.voltaje],
                ['Amperaje', eq.amperaje ? `${eq.amperaje} A` : null],
                ['Tipo de conector', eq.tipoConector],
                ['Tipo de inflador', eq.tipoInflador],
                ['Potencia', eq.potencia ? `${eq.potencia} W` : null],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={S.dataItem}>
                  <span style={S.dataLabel}>{label}</span>
                  <span style={S.dataValue}>{value}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {eq.restricciones && (
          <>
            <div style={S.sectionTitle}>⚠️ Restricciones de uso</div>
            <div style={{ background: '#fef9c3', border: '1px solid #fbbf24', borderRadius: 8, padding: '12px 16px', fontSize: 12, color: '#92400e', lineHeight: 1.7 }}>
              {eq.restricciones}
            </div>
          </>
        )}

        {(eq.observacionesOp || eq.notas) && (
          <>
            <div style={S.sectionTitle}>📝 Observaciones</div>
            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
              {eq.observacionesOp && <div>{eq.observacionesOp}</div>}
              {eq.notas && eq.notas !== eq.observacionesOp && <div style={{ marginTop: eq.observacionesOp ? 6 : 0 }}>{eq.notas}</div>}
            </div>
          </>
        )}

        <div style={{ marginTop: 24, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#166534' }}>
            ✅ Este equipo es operado por personal capacitado de INFLABLES DA SILVA bajo los protocolos de seguridad vigentes.
          </div>
        </div>
      </div>
      <PageFooter text={`Ficha Técnica — ${eq.nombre}`} />
    </div>
  ));
}

export default function SafetyPackagePage() {
  const [certs, setCerts] = useState([]);
  const [equiposInventario, setEquiposInventario] = useState([]);
  const [maintenanceSummary, setMaintenanceSummary] = useState({ totalEquipos: 0, totalRegistros: 0, registros: [] });
  const [recentChecklists, setRecentChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  const params = new URLSearchParams(window.location.search);
  const equiposParam = params.get('equipos') || '';
  const nombresParam = params.get('nombres') || '';
  const filtrado = equiposParam.length > 0 || nombresParam.length > 0;

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = PRINT_CSS;
    document.head.appendChild(style);

    const qs = new URLSearchParams();
    if (equiposParam) qs.set('equipos', equiposParam);
    if (nombresParam) qs.set('nombres', nombresParam);
    const url = `${API}/legal-docs/safety-package${qs.toString() ? '?' + qs.toString() : ''}`;

    fetch(url)
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          setCerts(d.certs || []);
          setEquiposInventario(d.equiposInventario || []);
          setMaintenanceSummary(d.maintenanceSummary || { totalEquipos: 0, totalRegistros: 0, registros: [] });
          setRecentChecklists(d.recentChecklists || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    return () => document.head.removeChild(style);
  }, []);

  const certPageNum = 5 + Math.ceil(certs.length / 3);

  return (
    <div style={S.wrapper}>
      {/* Controles (no se imprimen) */}
      <div style={S.controls} className="no-print">
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#1e1b4b' }}>📋 Paquete Oficial de Seguridad</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            {filtrado
              ? `📌 Paquete personalizado — ${certs.length} equipo(s) seleccionado(s)`
              : `Imprimible como PDF — ${certs.length} equipo(s) certificado(s)`}
          </div>
        </div>
        <button style={S.btnPrint} onClick={() => window.print()}>
          🖨️ Imprimir / Guardar PDF
        </button>
        <button style={S.btnClose} onClick={() => window.close()}>
          ✕ Cerrar
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 16 }}>
          Cargando documento...
        </div>
      ) : (
        <>
          {/* Página 1: Portada */}
          <PaginaPortada />

          {/* Página 2: Protocolo */}
          <PaginaProtocolo />

          {/* Página 3: Normas de uso */}
          <PaginaNormas />

          {/* Página 4: Póliza de Responsabilidad Civil */}
          <PaginaPoliza />

          {/* Páginas 5+: Fichas técnicas de equipos del evento */}
          {equiposInventario.length > 0 && (
            <PaginaFichasTecnicas equipos={equiposInventario} startPageNum={5} />
          )}

          {/* Certificados formales (si existen) */}
          <PaginaCertificados certs={certs} />

          {/* Bitácora de mantenimiento — Res. 958/2010 */}
          <PaginaBitacora
            summary={maintenanceSummary}
            pageNum={5 + equiposInventario.length + Math.ceil(certs.length / 3)} />

          {/* Checklists pre-evento — Res. 958/2010 Art. 26 */}
          <PaginaChecklists
            checklists={recentChecklists}
            pageNum={6 + equiposInventario.length + Math.ceil(certs.length / 3)} />

          {/* Última página: Declaración oficial */}
          <PaginaFinal pageNum={7 + equiposInventario.length + Math.ceil(certs.length / 3)} />
        </>
      )}
    </div>
  );
}
